import {
  Button,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Text,
  useToast,
} from '@chakra-ui/react';
import firebase from 'firebase/app';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { LogIn, UserPlus } from 'react-feather';
import { useForm } from 'react-hook-form';
import Lottie from 'react-lottie';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import animationData from '../../../assets/animations/lifter.json';
import { Card } from '../../../components/Card';
import { AuthContainer } from '../../../components/container/AuthContainer';
import { PageContainer } from '../../../components/container/PageContainer';
import { useConfig } from '../../../hooks/useConfig';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const MotionIcon = motion(Icon);

type FormData = {
  email: string;
  password: string;
};

export const LoginPage = () => {
  const { register, handleSubmit, formState } = useForm<FormData>();
  const { errors } = formState;
  const auth = firebase.auth();
  const [loading, setLoading] = useState(false);
  const params = useParams<{ redirectUrl?: string }>();
  const redirectUrl = params.redirectUrl
    ? `/${params.redirectUrl.split('_SLASH').join('/')}`
    : '/';
  const history = useHistory();
  const toast = useToast();
  const currentUser = useCurrentUser();
  const firebaseConfig = useConfig();

  const onSubmit = handleSubmit(({ email, password }) => {
    setLoading(true);
    auth
      .signInWithEmailAndPassword(email, password)
      .then(() => {
        setLoading(false);
      })
      .catch(async (e: firebase.auth.AuthError) => {
        try {
          await firebase
            .firestore()
            .collection('errors')
            .add({ id: 'loginUser', error: JSON.stringify(e) });
        } catch (err) {
          console.error(err);
        }
        if (e.code === 'auth/invalid-email') {
          toast({
            title: 'Fehler bei der Anmeldung',
            description: 'Deine E-Mail scheint falsch zu sein.',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
        if (e.code === 'auth/user-disabled') {
          toast({
            title: 'Fehler bei der Anmeldung',
            description: 'Du wurdest deaktiviert. Bitte melde dich bei uns.',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
        if (e.code === 'auth/user-not-found') {
          toast({
            title: 'Fehler bei der Anmeldung',
            description:
              'Wir konnten dich nicht finden, bitte überprüfe deine E-Mail Adresse oder registriere dich!',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
        if (e.code === 'auth/wrong-password') {
          toast({
            title: 'Fehler bei der Anmeldung',
            description:
              'Dein Passwort scheint falsch zu sein, bitte überprüfe es!',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
        setLoading(false);
      });
  });

  if (currentUser) return <Redirect to={redirectUrl} />;

  return (
    <PageContainer center>
      <AuthContainer
        title="Next level online-fitness"
        subtitle={`in deinem ${firebaseConfig?.displayName}`}
        icon={
          <Lottie
            isClickToPauseDisabled={true}
            options={{
              loop: true,
              autoplay: true,
              animationData,
              rendererSettings: {
                preserveAspectRatio: 'xMidYMid slice',
              },
            }}
            style={{ width: '33%', height: 'auto' }}
          />
        }
      >
        <Card title="Login" icon={LogIn} animate={true}>
          <Text my={3} align="center" textAlign="center">
            Bitte erstelle zuerst ein neues Konto:
          </Text>
          <Button
            colorScheme="green"
            focusBorderColor="brand.500"
            width="full"
            my={3}
            leftIcon={
              <MotionIcon
                animate={{ rotate: [0, 5, -5, 0, 0, 0] }}
                transition={{ repeat: Infinity }}
                as={UserPlus}
              />
            }
            onClick={() => history.push('/register')}
          >
            Neues Konto erstellen
          </Button>
          <Text mt={6} mb={4} align="center" textAlign="center">
            Logge dich hier ein, wenn du bereits ein Konto erstellt hast.
          </Text>
          <form onSubmit={onSubmit}>
            <FormControl isInvalid={!!errors.email}>
              <FormLabel>E-Mail</FormLabel>
              <Input
                autoFocus={true}
                focusBorderColor="brand.500"
                type="email"
                placeholder="jemand@jemand.de"
                {...register('email', { required: true })}
              />
            </FormControl>
            <FormControl mt={3} isInvalid={!!errors.password}>
              <FormLabel>Passwort</FormLabel>
              <Input
                focusBorderColor="brand.500"
                type="password"
                placeholder="*******"
                {...register('password', { required: true })}
              />
            </FormControl>
            <Button
              focusBorderColor="brand.500"
              colorScheme="brand"
              width="full"
              mt={4}
              type="submit"
              isLoading={loading}
              leftIcon={<Icon as={LogIn} />}
            >
              Login
            </Button>
            <Button
              focusBorderColor="brand.500"
              my={3}
              width="full"
              variant="link"
              onClick={() => history.push('/password-reset')}
            >
              Passwort vergessen?
            </Button>
          </form>
        </Card>
      </AuthContainer>
    </PageContainer>
  );
};
